import customCss from '@/app/components/hotel-detail/css/customCss';
import { clientAppLog } from '@/app/components/search/logging/clientAppLog.ts';
import { applyImageTransformation, isImageUrlTransformable } from '@/app/components/search/utils/images.ts';

type ImageSourceType = {
  url: string;
  sources: { srcSet: string; media?: string }[];
};

export const transformationMobileRoomPanelLayout = `w_340,h_300,c_fill,q_auto,f_auto`;
export const transformationDesktopRoomPanelLayout = `w_600,h_300,c_fill,q_auto,f_auto`;

// HeroGallery X
export const transformationMobileLayout = 'w_340,h_300,c_lfill,q_auto,f_auto,g_auto';

/**
 * One box the hero paints, at the size its own gallery reserves for it — not a generic thumbnail.
 * Asking the image service for the shape actually painted is what lets `g_auto` frame the photo for
 * that shape; ask for a different one and `object-fit: cover` crops the framing back off.
 *
 * `media` gates the `<source>`; `preloadMedia` gates the preload link, where the ranges have to be
 * mutually exclusive — two matching links fetch two images at high priority — and also gap-free, or
 * a viewport that falls between them gets no preload at all. Hence the `.98` upper bounds: a
 * viewport width can be fractional (a zoomed page, or Windows display scaling).
 */
export type HeroBox = { media?: string; preloadMedia: string; height: number; ratio: number };

const upTo = (breakpoint: number) => `(max-width: ${breakpoint - 0.02}px)`;

/**
 * The evo hero reserves 200px up to `lg` and 328px from there, and does not change height again, so
 * two boxes describe it completely. Its width does keep growing to a cap: the gallery and the info
 * panel are both `flex: 1 1 50%` inside a column capped at 737px, so 368px is the widest it is ever
 * painted, and the box is that shape rather than the results card's taller 356x260 one.
 */
const evoHeroDesktopWidth = 368;

export const evoHeroBoxes: HeroBox[] = [
  {
    media: customCss.breakpoints.lg,
    preloadMedia: customCss.breakpoints.lg,
    height: 328,
    ratio: evoHeroDesktopWidth / 328,
  },
  {
    preloadMedia: upTo(customCss.breakpointsValues.lg),
    height: 200,
    // Full-bleed against the 200px reserved below `lg`, which is 2:1 at the phone widths it covers.
    ratio: 2,
  },
];

/**
 * The classic hero's smallest box: full-bleed by the 260px its container reserves. It keeps the
 * tablet and desktop grid entries below, which describe a layout the evo hero does not have.
 */
const classicHeroMobileBox: HeroBox = {
  preloadMedia: upTo(customCss.breakpointsValues.sm),
  height: 260,
  ratio: 400 / 260,
};

const BOOKING_IMAGE_HOST = 'bstatic.com';

/**
 * Booking's own images carry their resolution in the path instead of in a transformation, so the
 * image service cannot reshape them into a box — they get this ladder whichever gallery asks.
 */
const isBookingImage = (url: string) => url.includes(BOOKING_IMAGE_HOST);

const bookingResolution = (url: string, resolution: string) => url.replace('/max500/', `/${resolution}/`);

const getBookingSources = (url: string) => [
  { srcSet: bookingResolution(url, 'max1024x768'), media: customCss.breakpoints.xl },
  { srcSet: bookingResolution(url, 'max800x600'), media: customCss.breakpoints.sm },
  { srcSet: bookingResolution(url, 'max640x480') },
];

const heroBoxTransformation = (box: HeroBox, scale: number, isTopSecret: boolean) =>
  `w_${Math.round(box.height * box.ratio) * scale},h_${box.height * scale},c_lfill,q_auto,f_auto,g_auto${
    isTopSecret ? ',e_blur:400' : ''
  }`;

const getHeroBoxSource = (url: string, box: HeroBox, isTopSecret: boolean) =>
  applyImageTransformation(url, heroBoxTransformation(box, 1, isTopSecret));

/**
 * A 1x/2x pair per box, as the results cards already serve. Without the 2x candidate a retina phone
 * had only the 1x to paint and upscaled it across the full width of the screen.
 */
const getHeroBoxSourceSet = (url: string, box: HeroBox, isTopSecret: boolean) => ({
  srcSet: `${getHeroBoxSource(url, box, isTopSecret)} 1x, ${applyImageTransformation(
    url,
    heroBoxTransformation(box, 2, isTopSecret)
  )} 2x`,
  media: box.media,
});

// transformations for 5 different layouts in tablet from 1 image to 5
const transformationsTabletLayout = [
  ['w_992,h_264,c_lfill,q_auto,f_auto,g_auto'],
  ['w_656,h_264,c_lfill,q_auto,f_auto,g_auto', 'w_328,h_264,c_lfill,q_auto,f_auto,g_auto'],
  [
    'w_656,h_264,c_lfill,q_auto,f_auto,g_auto',
    'w_328,h_128,c_lfill,q_auto,f_auto,g_auto',
    'w_328,h_128,c_lfill,q_auto,f_auto,g_auto',
  ],
  [
    'w_488,h_264,c_lfill,q_auto,f_auto,g_auto',
    'w_244,h_264,c_lfill,q_auto,f_auto,g_auto',
    'w_244,h_128,c_lfill,q_auto,f_auto,g_auto',
    'w_244,h_128,c_lfill,q_auto,f_auto,g_auto',
  ],
  [
    'w_488,h_264,c_lfill,q_auto,f_auto,g_auto',
    'w_244,h_128,c_lfill,q_auto,f_auto,g_auto',
    'w_244,h_128,c_lfill,q_auto,f_auto,g_auto',
    'w_244,h_128,c_lfill,q_auto,f_auto,g_auto',
    'w_244,h_128,c_lfill,q_auto,f_auto,g_auto',
  ],
];
// transformations for 5 different layouts in desktop from 1 image to 5
const transformationsDesktopLayout = [
  ['w_1000,h_400,c_lfill,q_auto,f_auto,g_auto'],
  ['w_661,h_400,c_lfill,q_auto,f_auto,g_auto', 'w_331,h_400,c_lfill,q_auto,f_auto,g_auto'],
  [
    'w_661,h_400,c_lfill,q_auto,f_auto,g_auto',
    'w_331,h_196,c_lfill,q_auto,f_auto,g_auto',
    'w_331,h_196,c_lfill,q_auto,f_auto,g_auto',
  ],
  [
    'w_492,h_400,c_lfill,q_auto,f_auto,g_auto',
    'w_246,h_400,c_lfill,q_auto,f_auto,g_auto',
    'w_246,h_196,c_lfill,q_auto,f_auto,g_auto',
    'w_246,h_196,c_lfill,q_auto,f_auto,g_auto',
  ],
  [
    'w_492,h_400,c_lfill,q_auto,f_auto,g_auto',
    'w_246,h_196,c_lfill,q_auto,f_auto,g_auto',
    'w_246,h_196,c_lfill,q_auto,f_auto,g_auto',
    'w_246,h_196,c_lfill,q_auto,f_auto,g_auto',
    'w_246,h_196,c_lfill,q_auto,f_auto,g_auto',
  ],
];

export function getSingleSource(url: string, transformation: string) {
  return applyImageTransformation(url, transformation);
}

export function getSourceMobile(url: string) {
  return url.includes('hotelbeds')
    ? url.replace(/original/, 'bigger')
    : applyImageTransformation(url, transformationMobileLayout);
}

type GalleryOptimizationProps = {
  images: string[];
  isMobile: boolean | undefined;
  isTopSecret: boolean;
  totalImages: number;
  /**
   * The boxes this gallery paints, largest breakpoint first — the browser takes the first `<source>`
   * that matches. Passing them replaces the per-image-count layout tables below, which described
   * the classic hero's grid rather than any box the evo hero has.
   */
  boxes?: HeroBox[];
};

type ResponsiveOption = {
  media: string;
  imageWidth: number;
  imageHeight: number;
  bookingResolution?: string;
};

export type ResponsiveOptions = ResponsiveOption[];
type ImageQuality = 'q_auto' | 'q_auto:best' | 'q_auto:eco' | 'q_auto:low';
type ImageCrop = 'c_fill' | 'c_fit' | 'c_lfill';

export const getImageSrc = ({
  url,
  responsiveOptions,
  mediaQuery,
  crop = 'c_fill',
  quality = 'q_auto',
}: {
  url: string;
  responsiveOptions: ResponsiveOptions;
  mediaQuery: string;
  crop?: ImageCrop;
  quality?: ImageQuality;
}) => {
  const responsiveOption = responsiveOptions.find((option) => option.media === mediaQuery);

  if (!responsiveOption) {
    // eslint-disable-next-line no-console
    console.error('ERROR: Responsive option not found for media query', mediaQuery);
    return '';
  }

  return isImageUrlTransformable(url)
    ? applyImageTransformation(
        url,
        `w_${responsiveOption?.imageWidth},h_${responsiveOption?.imageHeight},${crop},${quality},f_auto`
      )
    : url.includes('hotelbeds') && responsiveOption && responsiveOption?.imageWidth < 1000
      ? `${url.replace(/original\//, responsiveOption?.imageWidth > 320 ? 'bigger/' : '')}
        `
      : // booking.com images
        url.includes('bstatic.com') && responsiveOption?.bookingResolution
        ? url.replace('/max500/', `/${responsiveOption.bookingResolution}/`)
        : url;
};

export const getImageSizes = ({ url, responsiveOptions }: { url: string; responsiveOptions: ResponsiveOptions }) =>
  isImageUrlTransformable(url)
    ? responsiveOptions.map((option) => `${option.media} ${option.imageWidth}px`).join(', ')
    : undefined;

export const getImageSrcSet = ({
  url,
  responsiveOptions,
  crop = 'c_fill',
  quality = 'q_auto',
}: {
  url: string;
  responsiveOptions: ResponsiveOptions;
  crop?: ImageCrop;
  quality?: ImageQuality;
}) => {
  if (isImageUrlTransformable(url)) {
    return responsiveOptions
      .map(
        (option) =>
          `${applyImageTransformation(
            url,
            `w_${option.imageWidth},h_${option.imageHeight},${crop},${quality},f_auto`
          )} ${option.imageWidth}w`
      )
      .join(', ');
  }

  if (url.includes('bstatic.com')) {
    return responsiveOptions
      .map((option) => `${url.replace('/max500/', `/${option.bookingResolution ?? 'max500'}/`)} ${option.imageWidth}w`)
      .join(', ');
  }

  return url;
};

export const getImageSources = ({
  url,
  responsiveOptions,
  crop = 'c_fill',
  quality = 'q_auto',
}: {
  url: string;
  responsiveOptions: ResponsiveOptions;
  crop?: ImageCrop;
  quality?: ImageQuality;
}) => {
  const sources = responsiveOptions.map((option) => ({
    srcSet:
      isBookingImage(url) && option.bookingResolution
        ? bookingResolution(url, option.bookingResolution)
        : applyImageTransformation(url, `w_${option.imageWidth},h_${option.imageHeight},${crop},${quality},f_auto`),
    media: option.media,
  }));

  /*
   * A url the service cannot resize comes back unchanged for every breakpoint, and a `<picture>` whose
   * candidates all repeat the `<img>` url has nothing to choose between: the `<img src>` already paints
   * it. Only the all-identical case is dropped — removing one middle candidate would change which image
   * a mid-size viewport paints, since the browser takes the first `<source>` that matches (MPH-628).
   */
  return sources.every((source) => source.srcSet === url) ? [] : sources;
};

function getTransformation(transformations: string[][], totalImages: number, currentImage: number): string {
  if (transformations.length < totalImages || transformations[totalImages].length < currentImage) {
    clientAppLog({
      level: 'WARN',
      codeSourceName: 'image-transformations',
      logMessage: `undefined transformation. Total images: ${totalImages}. CurrentImage: ${currentImage}. Transformations: ${transformations}`,
    });
    return transformations[transformations.length - 1][transformations[transformations.length - 1].length - 1];
  }
  return transformations[totalImages][currentImage];
}

export function getGallerySourceOptimization(props: GalleryOptimizationProps): ImageSourceType[] {
  const { images, isMobile, isTopSecret, totalImages, boxes } = props;

  const numberOfImagesIndex = Math.min(totalImages, 5) - 1;

  // A gallery that knows its own boxes needs nothing else: the media queries pick the candidate, so
  // there is no `isMobile` to get wrong between the server render and the client's. Booking's urls
  // still take their own ladder — a box means nothing to a host that cannot transform.
  if (boxes?.length) {
    const smallest = boxes[boxes.length - 1];

    return images.map((url) =>
      isBookingImage(url)
        ? { url, sources: getBookingSources(url) }
        : {
            url: getHeroBoxSource(url, smallest, isTopSecret),
            sources: isImageUrlTransformable(url) ? boxes.map((box) => getHeroBoxSourceSet(url, box, isTopSecret)) : [],
          }
    );
  }

  const optimizedSource = images.map((url, index) => ({
    url: getHeroBoxSource(url, classicHeroMobileBox, isTopSecret),
    sources: isBookingImage(url)
      ? getBookingSources(url)
      : !isImageUrlTransformable(url)
        ? []
        : isMobile
          ? [getHeroBoxSourceSet(url, classicHeroMobileBox, isTopSecret)]
          : [
              {
                srcSet: applyImageTransformation(
                  url,
                  isTopSecret
                    ? `${getTransformation(transformationsDesktopLayout, numberOfImagesIndex, index)},e_blur:400`
                    : getTransformation(transformationsDesktopLayout, numberOfImagesIndex, index)
                ),
                media: customCss.breakpoints.xl,
              },
              {
                srcSet: applyImageTransformation(
                  url,
                  isTopSecret
                    ? `${getTransformation(transformationsTabletLayout, numberOfImagesIndex, index)},e_blur:400`
                    : getTransformation(transformationsTabletLayout, numberOfImagesIndex, index)
                ),
                media: customCss.breakpoints.sm,
              },
              getHeroBoxSourceSet(url, classicHeroMobileBox, isTopSecret),
            ],
  }));

  return optimizedSource;
}

type HeroImageSourcePreloadProps = {
  url: string;
  isTopSecret: boolean;
  totalImages: number;
  /** Same boxes the gallery renders, so the preloaded candidate is the one the hero then paints. */
  boxes?: HeroBox[];
  /** Same flag the gallery is given: on a mobile layout it paints the mobile box at every width. */
  isMobile?: boolean;
};

type HeroImagePreload = { href: string; imageSrcset?: string; media?: string };

/*
 * The candidate the hero is about to paint, as a preload link, so the browser starts it before it
 * has read the markup. Every entry has to be the image the gallery really shows at that width: a
 * preload the hero never paints is a full-size download at high priority, thrown away.
 */
export function getHeroImageSourcePreload(props: HeroImageSourcePreloadProps): HeroImagePreload[] {
  const { url, isTopSecret, totalImages, boxes, isMobile } = props;

  if (!url) return [];

  // Booking's ladder, on ranges narrowed so they cannot both match — the `<source>` ranges overlap
  // and rely on source order, which a preload link has no equivalent of.
  if (isBookingImage(url)) {
    return [
      { href: bookingResolution(url, 'max640x480'), media: upTo(customCss.breakpointsValues.sm) },
      { href: bookingResolution(url, 'max800x600'), media: customCss.breakpoints.smOnly },
      { href: bookingResolution(url, 'max1024x768'), media: customCss.breakpoints.xl },
    ];
  }

  if (boxes?.length && isImageUrlTransformable(url)) {
    return boxes.map((box) => ({
      // `href` is what a browser falls back to when it cannot read `imagesrcset`, so it has to be a
      // sized candidate too — pointing it at the raw url would preload the multi-megabyte original.
      href: getHeroBoxSource(url, box, isTopSecret),
      imageSrcset: getHeroBoxSourceSet(url, box, isTopSecret).srcSet,
      media: box.preloadMedia,
    }));
  }

  // Nothing to size: the `<img>` paints this very url, so preloading it is the same bytes, earlier.
  if (!isImageUrlTransformable(url)) return [{ href: url }];

  const mobile = {
    href: getHeroBoxSource(url, classicHeroMobileBox, isTopSecret),
    imageSrcset: getHeroBoxSourceSet(url, classicHeroMobileBox, isTopSecret).srcSet,
  };

  /*
   * A mobile layout paints the mobile box at every width — the one `<source>` it renders carries no
   * media — so the tablet and desktop candidates below would be fetched at high priority and never
   * shown. Left un-gated this costs a whole extra hero image on a phone held in landscape.
   */
  if (isMobile) return [mobile];

  const numberOfImagesIndex = Math.min(totalImages, 5) - 1;
  const forLayout = (transformations: string[][]) => {
    const transformation = getTransformation(transformations, numberOfImagesIndex, 0);

    return applyImageTransformation(url, isTopSecret ? `${transformation},e_blur:400` : transformation);
  };
  const tablet = forLayout(transformationsTabletLayout);
  const desktop = forLayout(transformationsDesktopLayout);

  return [
    { ...mobile, media: classicHeroMobileBox.preloadMedia },
    { href: tablet, imageSrcset: tablet, media: customCss.breakpoints.smOnly },
    { href: desktop, imageSrcset: desktop, media: customCss.breakpoints.xl },
  ];
}
